import { EventCriticality, EventResourceType } from '../chunk-JMXOIWNZ.js';
import { mergeMatrices, TEAM_AUDIT, TEAM_INVITES, TEAM_ROLES, TEAM_MEMBERS, TEAM, COLLABORATIVE_RESOURCES } from '../chunk-YD4SPRIM.js';
export { COLLABORATIVE_RESOURCES, TEAM, TEAM_AUDIT, TEAM_INVITES, TEAM_MANAGEMENT_RESOURCES, TEAM_MEMBERS, TEAM_ROLES } from '../chunk-YD4SPRIM.js';
import '../chunk-OZHLQIE6.js';
import { permissionSetFromRolePermissions } from '../chunk-XPFKWJZ2.js';
import '../chunk-N2XBXBPE.js';
import '../chunk-3KBLIIIM.js';
import '../chunk-XR6TNFQL.js';
import '../chunk-HIWBYL4O.js';
import '../chunk-PCIOFMXB.js';
import '../chunk-X34AGPO3.js';
import '../chunk-L6V3D5WY.js';
import '../chunk-7B3IWQPL.js';
import '../chunk-5WIUKZF6.js';
import '../chunk-HCZGW64N.js';
import '../chunk-QREXYHAX.js';
import '../chunk-2FJIJRTO.js';
import '../chunk-BFUJOKOR.js';
import '../chunk-DQNP4KXT.js';
import '../chunk-WVLHT3DL.js';
import '../chunk-E27HBF5G.js';
import { TenantContext } from '../chunk-YXYPU4PW.js';
import '../chunk-BJ3WKLB6.js';
import '../chunk-INX63VAT.js';
import '../chunk-R6JE7FJZ.js';
import '../chunk-UDOQQOJI.js';
import '../chunk-3QN2VZ56.js';
import '../chunk-7PG254B4.js';
import '../chunk-YA25Q2WS.js';
import '../chunk-O2TUCS6S.js';
import { initials } from '../chunk-T3LQT4VP.js';
import '../chunk-ARJPXDVG.js';
import '../chunk-AHISJLEK.js';
import '../chunk-QXYP3KHC.js';
import '../chunk-AKIWOWOF.js';
import '../chunk-R5ZQC5YM.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-RAO7SHWG.js';
import '../chunk-SAW4RQG7.js';
import '../chunk-PZ5AY32C.js';

// src/teams/team.ts
var TEAM_ID_PREFIX = "team_";
function isTeamId(value) {
  const trimmed = value.trim();
  return trimmed.startsWith(TEAM_ID_PREFIX) && trimmed.length > TEAM_ID_PREFIX.length;
}
function notTeamIds(raw) {
  return raw.split(/[\s,]+/).map((entry) => entry.trim()).filter(Boolean).filter((entry) => !isTeamId(entry));
}
function teamTenantKey(team) {
  return new TenantContext(team.tenant).orgIdTenantId;
}
function teamKey(team) {
  return `${teamTenantKey(team)}:${team.id}`;
}
function sameTeam(a, b) {
  return teamKey(a) === teamKey(b);
}

// src/teams/member.ts
var MemberStatus = {
  ACTIVE: "active",
  /** Invited but hasn't accepted yet (mirrors a pending invite). */
  INVITED: "invited",
  /** Access revoked but record retained. */
  SUSPENDED: "suspended"
};
var MEMBER_STATUS_LABEL = {
  active: "Active",
  invited: "Invited",
  suspended: "Suspended"
};
function memberInitials(member) {
  return initials(member.name?.trim() || member.email?.split("@")[0] || "");
}

// src/teams/role.ts
var TeamResource = {
  MEMBERS: "team_members",
  ROLES: "team_roles",
  INVITES: "team_invites",
  AUDIT: "team_audit",
  TEAM: "team"
};
var TEAM_COLLABORATIVE_RESOURCES = [
  "transactions",
  "addresses",
  "entities",
  "tags",
  "labels",
  "groups",
  "disputes",
  "streams",
  "pipelines",
  "workspaces",
  "notifications"
];
var TEAM_ROLE_GROUPS = /* @__PURE__ */ new Set([
  ...Object.values(TeamResource),
  ...TEAM_COLLABORATIVE_RESOURCES
]);
function grantableTeamScopes(catalogue) {
  return catalogue.filter((scope) => TEAM_ROLE_GROUPS.has(scope.resource));
}
function rolesForMember(member, roles) {
  const byId = new Map(roles.map((role) => [role.id, role]));
  return member.roleIds.map((id) => byId.get(id)).filter((role) => Boolean(role));
}
function effectiveMatrix(member, roles) {
  return mergeMatrices(...rolesForMember(member, roles).map((role) => role.permissions));
}
function memberPermissionSet(member, roles) {
  return permissionSetFromRolePermissions(effectiveMatrix(member, roles));
}

// src/teams/system-roles.ts
var TeamRoleKey = {
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member",
  VIEWER: "viewer"
};
var READ = ["read"];
var CONTRIBUTOR = ["read", "create", "update"];
var CRUD = ["read", "create", "update", "delete"];
function collaborativeMatrix(actions) {
  const out = {};
  for (const group of COLLABORATIVE_RESOURCES) out[group] = [...actions];
  return out;
}
function systemRoleSpecs() {
  return [
    {
      key: TeamRoleKey.OWNER,
      role: {
        id: "owner",
        name: "Owner",
        description: "Full control of the team, members, roles, invites and all team objects.",
        system: true,
        permissions: {
          [TEAM]: [...CRUD],
          [TEAM_MEMBERS]: [...CRUD],
          [TEAM_ROLES]: [...CRUD],
          [TEAM_INVITES]: [...CRUD],
          [TEAM_AUDIT]: [...READ],
          ...collaborativeMatrix(CRUD)
        }
      }
    },
    {
      key: TeamRoleKey.ADMIN,
      role: {
        id: "admin",
        name: "Admin",
        description: "Manage members, roles, invites and all team objects; edit team settings.",
        system: true,
        permissions: {
          [TEAM]: ["read", "update"],
          [TEAM_MEMBERS]: [...CRUD],
          [TEAM_ROLES]: [...CRUD],
          [TEAM_INVITES]: [...CRUD],
          [TEAM_AUDIT]: [...READ],
          ...collaborativeMatrix(CRUD)
        }
      }
    },
    {
      key: TeamRoleKey.MEMBER,
      role: {
        id: "member",
        name: "Member",
        description: "Standard team member: read the team + contribute to team objects.",
        system: true,
        permissions: {
          [TEAM]: [...READ],
          [TEAM_MEMBERS]: [...READ],
          [TEAM_ROLES]: [...READ],
          ...collaborativeMatrix(CONTRIBUTOR)
        }
      }
    },
    {
      key: TeamRoleKey.VIEWER,
      role: {
        id: "viewer",
        name: "Viewer",
        description: "Read-only access to the team and its objects.",
        system: true,
        permissions: {
          [TEAM]: [...READ],
          // Seeing who is on your own team, and what their roles are called, is
          // part of reading the team (team_vocabulary.py, 2026-10-01).
          [TEAM_MEMBERS]: [...READ],
          [TEAM_ROLES]: [...READ],
          ...collaborativeMatrix(READ)
        }
      }
    }
  ];
}
var SPEC_ROLE_ALIASES = {
  org_admin: TeamRoleKey.OWNER,
  tenant_admin: TeamRoleKey.ADMIN,
  writer: TeamRoleKey.MEMBER,
  reader: TeamRoleKey.VIEWER
};
var CANONICAL_KEYS = new Set(Object.values(TeamRoleKey));
function resolveRoleKey(name) {
  const normalized = name.trim().toLowerCase();
  if (CANONICAL_KEYS.has(normalized)) return normalized;
  return SPEC_ROLE_ALIASES[normalized];
}
function systemRoleFor(name) {
  const key = resolveRoleKey(name);
  if (!key) return void 0;
  return systemRoleSpecs().find((spec) => spec.key === key)?.role;
}

// src/teams/invite.ts
var InviteStatus = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  REVOKED: "revoked",
  EXPIRED: "expired"
};
var INVITE_STATUS_LABEL = {
  pending: "Pending",
  accepted: "Accepted",
  revoked: "Revoked",
  expired: "Expired"
};
var INVITE_EMAIL_NOT_SENT = "The invitation is saved, but its email couldn't be sent. Copy its acceptance link, or renew it from the Invites tab.";
var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email) {
  return EMAIL_RE.test(email.trim());
}
function isInviteActionable(invite) {
  return invite.status === InviteStatus.PENDING || invite.status === InviteStatus.EXPIRED;
}

// src/teams/audit.ts
var SEVERITY_TO_CRITICALITY = {
  critical: EventCriticality.CRITICAL,
  high: EventCriticality.HIGH,
  medium: EventCriticality.MEDIUM,
  low: EventCriticality.LOW,
  success: EventCriticality.SUCCESS
};
function inferSeverity(action) {
  if (/remove|delete|revoke|suspend/.test(action)) return "high";
  if (/create|invite|add|accept|grant/.test(action)) return "success";
  return "low";
}
function toActivityLogEvents(entries) {
  return entries.map((entry) => ({
    id: entry.id,
    created_at: entry.at,
    resource: EventResourceType.ACTIVITY_LOG,
    criticality_level: SEVERITY_TO_CRITICALITY[entry.severity ?? inferSeverity(entry.action)],
    message_summary: entry.summary,
    function_name: entry.action,
    payload_summary: entry.target,
    created_by: entry.actor ?? null,
    metadata: entry.metadata
  }));
}

export { CONTRIBUTOR, CRUD, INVITE_EMAIL_NOT_SENT, INVITE_STATUS_LABEL, InviteStatus, MEMBER_STATUS_LABEL, MemberStatus, READ, SPEC_ROLE_ALIASES, TEAM_COLLABORATIVE_RESOURCES, TEAM_ID_PREFIX, TEAM_ROLE_GROUPS, TeamResource, TeamRoleKey, effectiveMatrix, grantableTeamScopes, isInviteActionable, isTeamId, isValidEmail, memberInitials, memberPermissionSet, notTeamIds, resolveRoleKey, rolesForMember, sameTeam, systemRoleFor, systemRoleSpecs, teamKey, teamTenantKey, toActivityLogEvents };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map